import React, { useState, useRef, useEffect } from 'react';
import { Terminal as TerminalIcon, Send } from 'lucide-react';
import { trackEvent } from '../utils/analytics';

interface CommandResult {
  command: string;
  response: React.ReactNode;
}

const Terminal: React.FC = () => {
  const [input, setInput] = useState<string>('');
  const [history, setHistory] = useState<CommandResult[]>([
    {
      command: 'help',
      response: (
        <div>
          <p className="text-green-400 mb-1">Available commands:</p>
          <ul className="text-gray-400 space-y-1">
            <li><span className="text-yellow-400">help</span> - Display available commands</li>
            <li><span className="text-yellow-400">about</span> - Learn more about me</li>
            <li><span className="text-yellow-400">skills</span> - List my technical skills</li>
            <li><span className="text-yellow-400">projects</span> - View my featured projects</li>
            <li><span className="text-yellow-400">certificates</span> - View my certifications</li>
            <li><span className="text-yellow-400">contact</span> - Get my contact information</li>
            <li><span className="text-yellow-400">socials</span> - List my social media profiles</li>
            <li><span className="text-yellow-400">clear</span> - Clear the terminal</li>
          </ul>
        </div>
      ),
    },
  ]);
  const [showCursor, setShowCursor] = useState<boolean>(true);
  const terminalRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Blinking cursor effect
  useEffect(() => {
    const interval = setInterval(() => {
      setShowCursor((prev) => !prev);
    }, 500);
    return () => clearInterval(interval);
  }, []);

  // Auto scroll to bottom
  useEffect(() => {
    if (terminalRef.current) {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
    }
  }, [history]);

  // Global keyboard shortcuts
  useEffect(() => {
    const handleGlobalKeydown = (e: KeyboardEvent) => {
      // Ctrl+T to focus terminal
      if (e.ctrlKey && e.key === 't') {
        e.preventDefault();
        e.stopPropagation();
        if (inputRef.current) {
          inputRef.current.focus();
          // Scroll to terminal section
          document.getElementById('terminal')?.scrollIntoView({ behavior: 'smooth' });
        }
      }
      // Escape key or any scroll attempt to blur terminal
      else if (e.key === 'Escape' ||
        e.key === 'ArrowUp' ||
        e.key === 'ArrowDown' ||
        e.key === 'PageUp' ||
        e.key === 'PageDown' ||
        e.key === 'Home' ||
        e.key === 'End') {
        if (document.activeElement === inputRef.current) {
          inputRef.current?.blur();
          // Allow the scroll/navigation event to proceed
          if (e.key !== 'Escape') {
            return; // Don't prevent default for navigation keys
          }
          e.preventDefault();
        }
      }
    };

    document.addEventListener('keydown', handleGlobalKeydown);
    return () => document.removeEventListener('keydown', handleGlobalKeydown);
  }, []);

  // Handle terminal container clicks
  const handleTerminalClick = (e: React.MouseEvent) => {
    // Only focus if clicking directly on the input or terminal content
    const target = e.target as HTMLElement;
    if ((target.tagName === 'INPUT' || target.closest('.terminal-content')) && inputRef.current) {
      inputRef.current.focus();
    }
  };

  // Handle wheel events to ensure page scrolling works
  const handleWheel = (e: React.WheelEvent) => {
    const target = e.currentTarget as HTMLDivElement;
    const { scrollTop, scrollHeight, clientHeight } = target;

    // If terminal is at top and scrolling up, or at bottom and scrolling down,
    // allow the event to bubble up for page scrolling
    if ((scrollTop === 0 && e.deltaY < 0) ||
      (scrollTop >= scrollHeight - clientHeight && e.deltaY > 0)) {
      // Don't prevent default, let it bubble up for page scroll
      return;
    }
  };

  // Remove scroll event handling completely to prevent scroll lock
  const handleTerminalScroll = () => {
    // Allow all scroll events to bubble up naturally
    // This prevents the terminal from trapping scroll events
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;

    const command = input.trim().toLowerCase();

    // Track terminal command usage
    trackEvent.terminalCommand(command);

    let result: React.ReactNode;

    switch (command) {
      case 'help':
        result = (
          <div>
            <p className="text-green-400 mb-1">Available commands:</p>
            <ul className="text-gray-400 space-y-1">
              <li><span className="text-yellow-400">help</span> - Display available commands</li>
              <li><span className="text-yellow-400">about</span> - Learn more about me</li>
              <li><span className="text-yellow-400">skills</span> - List my technical skills</li>
              <li><span className="text-yellow-400">projects</span> - View my featured projects</li>
              <li><span className="text-yellow-400">certificates</span> - View my certifications</li>
              <li><span className="text-yellow-400">contact</span> - Get my contact information</li>
              <li><span className="text-yellow-400">socials</span> - List my social media profiles</li>
              <li><span className="text-yellow-400">clear</span> - Clear the terminal</li>
            </ul>
          </div>
        );
        break;
      case 'about':
        result = (
          <div>
            <p className="text-blue-400 mb-2">About S.SRIVARUN:</p>
            <p className="text-gray-300">
              Passionate software engineer with a strong foundation in AI, machine learning, and distributed systems.
              Experienced in building scalable Generative AI applications using Python, LangChain, and RAG pipelines.
              Focused on solving real-world business problems and creating intelligent systems that deliver measurable impact.
            </p>
          </div>
        );
        break;
      case 'skills':
        result = (
          <div>
            <p className="text-blue-400 mb-2">Technical Skills:</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1">
              <span className="text-purple-400">Python</span>
              <span className="text-purple-400">LangChain</span>
              <span className="text-blue-400">TensorFlow</span>
              <span className="text-blue-400">Scikit-learn</span>
              <span className="text-indigo-400">RAG Pipelines</span>
              <span className="text-indigo-400">Generative AI</span>
              <span className="text-purple-400">Multi-Agent AI</span>
              <span className="text-purple-400">NumPy / Pandas</span>
              <span className="text-blue-400">Deep Learning</span>
              <span className="text-blue-400">Data Science</span>
              <span className="text-indigo-400">MongoDB</span>
              <span className="text-indigo-400">Supabase</span>
            </div>
          </div>
        );
        break;
      case 'projects':
        result = (
          <div>
            <p className="text-blue-400 mb-2">Featured Projects:</p>
            <ul className="text-gray-300 space-y-1">
              <li><span className="text-yellow-400">→</span> Autonomous AI Trading System</li>
              <li><span className="text-yellow-400">→</span> AI Interviewer System</li>
              <li><span className="text-yellow-400">→</span> Personalized AI Roadmap Generator</li>
              <li><span className="text-yellow-400">→</span> DataCraft AI Studio</li>
              <li><span className="text-yellow-400">→</span> Personal & Family Finance App</li>
              <li><span className="text-yellow-400">→</span> Visual Roadmap Tracker</li>
              <li><span className="text-yellow-400">→</span> TechFest - Event Management System</li>
            </ul>
            <p className="text-gray-400 mt-2">Scroll up to the <span className="text-green-400">Projects section</span> for more details and live demos.</p>
          </div>
        );
        break;
      case 'certificates':
        result = (
          <div>
            <p className="text-blue-400 mb-2">Professional Certifications:</p>
            <ul className="text-gray-300 space-y-1">
              <li><span className="text-yellow-400">→</span> Deep Learning with Keras and TensorFlow</li>
              <li><span className="text-yellow-400">→</span> Database Management Systems</li>
              <li><span className="text-yellow-400">→</span> Introduction to Data Mining</li>
            </ul>
            <p className="text-gray-400 mt-2">Scroll up to the <span className="text-green-400">Certificates section</span> to view detailed information and certificate previews.</p>
          </div>
        );
        break;
      case 'contact':
        result = (
          <div>
            <p className="text-blue-400 mb-2">Contact Information:</p>
            <p className="text-gray-300">
              <span className="text-yellow-400">Email:</span> samayasri2005@gmail.com<br />
              <span className="text-yellow-400">Phone:</span> +91 9108818133<br />
              <span className="text-yellow-400">Location:</span> Bangalore, Karnataka
            </p>
            <p className="text-gray-400 mt-2">Scroll down to the contact section to send me a message.</p>
          </div>
        );
        break;
      case 'socials':
        result = (
          <div>
            <p className="text-blue-400 mb-2">Social Profiles:</p>
            <ul className="text-gray-300">
              <li><span className="text-yellow-400">GitHub:</span> github.com/samayasri27</li>
              <li><span className="text-yellow-400">LinkedIn:</span> linkedin.com/in/srivarun05</li>
              <li><span className="text-yellow-400">Email:</span> samayasri2005@gmail.com</li>
            </ul>
          </div>
        );
        break;
      case 'clear':
        setHistory([]);
        setInput('');
        return;
      default:
        result = (
          <p className="text-red-400">
            Command not recognized: '{command}'. Type 'help' for available commands.
          </p>
        );
    }

    setHistory((prev) => [...prev, { command, response: result }]);
    setInput('');
  };

  return (
    <section id="terminal" className="py-24 bg-gradient-to-b from-black to-purple-950/5 relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 bg-gradient-to-b from-purple-950/10 to-black"></div>
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold font-orbitron mb-4 tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-500">
            Command Center
          </h2>
          <div className="h-1 w-20 bg-gradient-to-r from-purple-500 to-blue-500 mx-auto rounded-full mb-8"></div>
          <p className="max-w-3xl mx-auto text-lg text-gray-300">
            Interact directly with my portfolio using terminal commands. Type 'help' to see available commands.
          </p>
          <p className="text-sm text-gray-400 mt-2">
            <kbd className="px-2 py-1 bg-gray-800 rounded-md border border-gray-700 text-gray-300">Ctrl</kbd> + <kbd className="px-2 py-1 bg-gray-800 rounded-md border border-gray-700 text-gray-300">T</kbd> to focus terminal • <kbd className="px-2 py-1 bg-gray-800 rounded-md border border-gray-700 text-gray-300">Esc</kbd> to exit terminal focus
          </p>
        </div>

        <div className="relative max-w-3xl mx-auto">
          {/* Terminal glow effect */}
          <div className="absolute -inset-0.5 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg opacity-50 blur-sm"></div>

          {/* Terminal window */}
          <div
            className="relative bg-gray-950 rounded-lg border border-purple-900/40 overflow-hidden shadow-xl"
            onClick={handleTerminalClick}
          >
            {/* Terminal header */}
            <div className="bg-gray-900 p-3 flex items-center border-b border-gray-800">
              <div className="flex space-x-2 mr-3">
                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              </div>
              <div className="flex items-center text-gray-400 text-sm">
                <TerminalIcon className="w-4 h-4 mr-2" />
                <span className="font-orbitron">srivarun@portfolio ~ </span>
              </div>
            </div>

            {/* Terminal body */}
            <div
              ref={terminalRef}
              className="terminal-body bg-gray-950 p-4 h-80 overflow-y-auto font-mono text-sm"
              onScroll={handleTerminalScroll}
              onWheel={handleWheel}
            >
              {/* Welcome message */}
              <div className="terminal-content mb-4">
                <p className="text-green-400 mb-1">Welcome to S.SRIVARUN Portfolio Terminal v1.0.0</p>
                <p className="text-gray-300">Type 'help' to see available commands.</p>
              </div>

              {/* Command history */}
              <div className="terminal-content">
                {history.map((item, index) => (
                  <div key={index} className="mb-4">
                    <div className="flex items-center text-gray-300 mb-1">
                      <span className="text-purple-400 mr-2">➜</span>
                      <span className="text-yellow-400">{item.command}</span>
                    </div>
                    <div className="ml-4 text-gray-300">
                      {item.response}
                    </div>
                  </div>
                ))}
              </div>

              {/* Input line */}
              <form onSubmit={handleSubmit} className="flex items-center terminal-content">
                <span className="text-purple-400 mr-2">➜</span>
                <div className="flex-1 flex items-center">
                  <input
                    ref={inputRef}
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    className="flex-1 bg-transparent text-yellow-400 outline-none border-none"
                    onFocus={() => {
                      // Auto-blur after a short delay to prevent scroll lock
                      setTimeout(() => {
                        if (document.activeElement === inputRef.current) {
                          // Check if user is trying to scroll
                          const checkScroll = () => {
                            if (inputRef.current && document.activeElement === inputRef.current) {
                              inputRef.current.blur();
                            }
                          };
                          // Auto-blur if no interaction for 10 seconds
                          setTimeout(checkScroll, 10000);
                        }
                      }, 100);
                    }}
                  />
                  {showCursor && (
                    <span className="w-2 h-5 bg-purple-500 animate-pulse"></span>
                  )}
                </div>
                <button
                  type="submit"
                  className="ml-2 text-gray-400 hover:text-purple-400 transition-colors"
                >
                  <Send className="h-4 w-4" />
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Terminal;